import type { StatRow } from '../../../Repository/Admin/AdminStatsRepository.js'

/** A categorical {name, value} datum for a bar chart. */
export interface Datum {
  name: string
  value: number
}

export interface Summary {
  count: number
  min: number
  max: number
  avg: number
  median: number
  p20: number
  p80: number
}

/** Count rows grouped by a column value (nulls bucketed as "(none)"), descending by count. */
export const countBy = (rows: StatRow[], key: string): Datum[] => {
  const counts = new Map<string, number>()
  for (const row of rows) {
    const value = row[key]
    const name = value == null || value === '' ? '(none)' : String(value)
    counts.set(name, (counts.get(name) ?? 0) + 1)
  }
  return [...counts.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value)
}

export const countWhere = (rows: StatRow[], predicate: (row: StatRow) => boolean): number => rows.filter(predicate).length

/** Count rows grouped by a caller-derived label (nulls bucketed as "(none)"), descending by count. */
export const countByLabel = (rows: StatRow[], label: (row: StatRow) => string | undefined): Datum[] => {
  const counts = new Map<string, number>()
  for (const row of rows) {
    const name = label(row) ?? '(none)'
    counts.set(name, (counts.get(name) ?? 0) + 1)
  }
  return [...counts.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value)
}

/** A short, axis-friendly label for an id (e.g. a UUID when no name resolved); undefined for nulls. */
export const shortId = (value: unknown): string | undefined => {
  if (value == null) return undefined
  const s = String(value)
  return s.length > 8 ? `${s.slice(0, 8)}…` : s
}

/** Keep the top n data points, folding the remainder into "(others)". */
export const takeTop = (data: Datum[], n: number): Datum[] => {
  if (data.length <= n) return data
  const rest = data.slice(n).reduce((acc, d) => acc + d.value, 0)
  const top = data.slice(0, n)
  return rest > 0 ? [...top, { name: '(others)', value: rest }] : top
}

/** Numeric values of a column (non-numbers and nulls dropped). */
export const numbersOf = (rows: StatRow[], key: string): number[] =>
  rows.map((row) => row[key]).filter((v): v is number => typeof v === 'number' && Number.isFinite(v))

const percentile = (sortedAsc: number[], p: number): number => {
  if (sortedAsc.length === 0) return 0
  const idx = (sortedAsc.length - 1) * p
  const lo = Math.floor(idx)
  const hi = Math.ceil(idx)
  if (lo === hi) return sortedAsc[lo]
  return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * (idx - lo)
}

export const summarize = (values: number[]): Summary => {
  const sorted = [...values].sort((a, b) => a - b)
  const count = sorted.length
  const sum = sorted.reduce((acc, v) => acc + v, 0)
  return {
    count,
    min: count ? sorted[0] : 0,
    max: count ? sorted[count - 1] : 0,
    avg: count ? sum / count : 0,
    median: percentile(sorted, 0.5),
    p20: percentile(sorted, 0.2),
    p80: percentile(sorted, 0.8)
  }
}

/** Sum a numeric column across rows. */
export const sumOf = (rows: StatRow[], key: string): number => numbersOf(rows, key).reduce((acc, v) => acc + v, 0)

/** A time window for a single chart "version": everything, one month, or a YYYY-MM range. */
export type Period = { kind: 'all' } | { kind: 'month'; month: string } | { kind: 'range'; from: string; to: string }

export const periodLabel = (period: Period): string => {
  if (period.kind === 'all') return 'Overall'
  if (period.kind === 'month') return period.month
  return `${period.from} – ${period.to}`
}

/** Distinct YYYY-MM values present in a timestamp column, chronological. */
export const availableMonths = (rows: StatRow[], dateKey: string): string[] => {
  const months = new Set<string>()
  for (const row of rows) {
    const raw = row[dateKey]
    if (raw != null) months.add(String(raw).slice(0, 7))
  }
  return [...months].sort()
}

/** Filter rows whose timestamp falls in the given period (string YYYY-MM comparison). */
export const filterByPeriod = (rows: StatRow[], dateKey: string, period: Period): StatRow[] => {
  if (period.kind === 'all') return rows
  return rows.filter((row) => {
    const raw = row[dateKey]
    if (raw == null) return false
    const month = String(raw).slice(0, 7)
    return period.kind === 'month' ? month === period.month : month >= period.from && month <= period.to
  })
}

/**
 * Bucket numeric values into a histogram, returned as bar data. Our stats are integer counts, so a
 * small integer range gets one bar per value (no fractional bins); a wide range gets equal-width
 * bins with integer edge labels.
 */
export const histogram = (values: number[], maxBins = 12): Datum[] => {
  if (values.length === 0) return []
  const min = Math.min(...values)
  const max = Math.max(...values)
  if (min === max) return [{ name: String(min), value: values.length }]
  const range = max - min
  const allInteger = values.every((v) => Number.isInteger(v))

  if (allInteger && range <= maxBins) {
    const bins = new Array<number>(range + 1).fill(0)
    for (const v of values) bins[v - min] += 1
    return bins.map((value, i) => ({ name: String(min + i), value }))
  }

  const width = range / maxBins
  const bins = new Array<number>(maxBins).fill(0)
  for (const v of values) bins[Math.min(maxBins - 1, Math.floor((v - min) / width))] += 1
  const edge = (n: number): string => (allInteger ? String(Math.round(n)) : n.toFixed(1))
  return bins.map((value, i) => ({ name: `${edge(min + i * width)}–${edge(min + (i + 1) * width)}`, value }))
}

/** Count rows per calendar month (YYYY-MM) from a timestamp column, chronological. */
export const monthlyCounts = (rows: StatRow[], dateKey: string): Datum[] => monthlySeries(rows, dateKey, (bucket) => bucket.length)

/**
 * Bucket rows by calendar month (YYYY-MM of a timestamp column) and apply a per-bucket aggregate —
 * the generic basis for any "by month" trend (counts, ratios, sums). Chronological.
 */
export const monthlySeries = (rows: StatRow[], dateKey: string, value: (bucket: StatRow[]) => number): Datum[] => {
  const buckets = new Map<string, StatRow[]>()
  for (const row of rows) {
    const raw = row[dateKey]
    if (raw == null) continue
    const month = String(raw).slice(0, 7)
    const bucket = buckets.get(month)
    if (bucket) bucket.push(row)
    else buckets.set(month, [row])
  }
  return [...buckets.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([name, bucket]) => ({ name, value: value(bucket) }))
}
